// src/pages/leave/components/modals/HolidayTable.jsx

import React from "react";
import { Calendar, Loader, Settings, Trash2 } from "lucide-react";
import { useVisibleColumns } from "../../../../../hooks/useVisibleColumns";
import { useAuth } from "../../../../../context/AuthContext";

const HolidayTable = ({
  theme, isRTL, language, t,
  holidayList, holidayLoading,
  holidayDeleteConfirmId,
  onStartEdit, onDeleteConfirm, onDeleteCancel, onDeleteExecute,
}) => {
  // ✅ Admin-level feature gating — registry "leaves.remove" ("Delete") and
  // "leaves.edit" ("Edit Holiday"). Bypassed roles (admin/superadmin) always pass.
  const { hasFeatureAccess } = useAuth();
  const canRemove = hasFeatureAccess("leaves", "remove");
  const canEdit = hasFeatureAccess("leaves", "edit");

  const colHeaders = [
    { key: "clientName", label: t.clientNameLabel },
    { key: "workingCompany", label: t.workingCompanyLabel },
    { key: "holidayName", label: t.holidayName },
    { key: "date", label: t.date },
    { key: "departments", label: isRTL ? "الأقسام" : "Departments" },
    { key: "actions", label: t.actions },
  ];

  // ✅ Admin-level column visibility (hiddenColumns registry). Header and body
  // both render from this filtered list so they can never get out of sync.
  // Bypassed roles (admin/superadmin) always see every column.
  const visibleColumns = useVisibleColumns("leaves", "holidayTable", colHeaders);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  const thStyle = {
    padding: "12px 16px",
    textAlign: isRTL ? "right" : "left",
    fontWeight: "700",
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
  };

  const tdStyle = {
    padding: "13px 16px",
    color: theme === "dark" ? "#e5e7eb" : "#374151",
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Render a compact department badge list.
   * null / [] → "All" badge
   * string[]  → up to 2 names + overflow count
   */
  const renderDepartments = (departments) => {
    const isDark = theme === "dark";
    const isEmpty = !departments || (Array.isArray(departments) && departments.length === 0);

    if (isEmpty) {
      return (
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          borderRadius: "999px",
          fontSize: "11px",
          fontWeight: "700",
          background: isDark ? "rgba(52,211,153,0.12)" : "rgba(5,150,105,0.1)",
          color: isDark ? "#34d399" : "#059669",
          whiteSpace: "nowrap",
        }}>
          {isRTL ? "الجميع" : "All"}
        </span>
      );
    }

    const shown = departments.slice(0, 2);
    const extra = departments.length - shown.length;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
        {shown.map((dept) => (
          <span
            key={dept}
            title={dept}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 8px",
              borderRadius: "999px",
              fontSize: "11px",
              fontWeight: "600",
              background: isDark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.1)",
              color: isDark ? "#93c5fd" : "#2563eb",
              maxWidth: "120px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {dept}
          </span>
        ))}
        {extra > 0 && (
          <span style={{
            fontSize: "11px",
            fontWeight: "700",
            color: isDark ? "rgba(255,255,255,0.45)" : "#6b7280",
          }}>
            +{extra}
          </span>
        )}
      </div>
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (holidayLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
        <Loader size={28} style={{ animation: "spin 1s linear infinite", marginBottom: "10px" }} />
        <div>{t.loading}</div>
      </div>
    );
  }

  // ── Empty ─────────────────────────────────────────────────────────────────
  if (holidayList.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 24px", color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af" }}>
        <Calendar size={40} style={{ marginBottom: "12px", opacity: 0.4 }} />
        <div style={{ fontSize: "15px", fontWeight: "600" }}>{t.noHolidaysFound}</div>
      </div>
    );
  }

  // ── Table ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      overflowX: "auto",
      borderRadius: "14px",
      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#f9fafb" }}>
            {visibleColumns.map((col) => <th key={col.key} style={thStyle}>{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {holidayList.map((holiday, idx) => (
            <tr
              key={holiday.id}
              style={{
                background: idx % 2 === 0
                  ? "transparent"
                  : (theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
                borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.05)" : "#f3f4f6"}`,
              }}
            >
              {/* Client */}
              {showColumn("clientName") && (
              <td style={{ ...tdStyle, fontWeight: "500", color: theme === "dark" ? "#e5e7eb" : "#111827" }}>
                {holiday.clientName || "—"}
              </td>
              )}

              {/* Company */}
              {showColumn("workingCompany") && (
              <td style={tdStyle}>{holiday.workingCompany || "—"}</td>
              )}

              {/* Name */}
              {showColumn("holidayName") && (
              <td style={tdStyle}>{holiday.name}</td>
              )}

              {/* Date */}
              {showColumn("date") && (
              <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                {holiday.date
                  ? new Date(holiday.date).toLocaleDateString(
                      language === "ar" ? "ar-EG" : "en-GB",
                      { day: "2-digit", month: "short", year: "numeric" },
                    )
                  : "—"}
              </td>
              )}

              {/* Departments */}
              {showColumn("departments") && (
              <td style={tdStyle}>
                {renderDepartments(holiday.departments)}
              </td>
              )}

              {/* Actions */}
              {showColumn("actions") && (
              <td style={tdStyle}>
                {holidayDeleteConfirmId === holiday.id ? (
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: "600" }}>
                      {t.confirmDeleteHoliday}
                    </span>
                    <button
                      onClick={() => onDeleteExecute(holiday.id)}
                      style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                    >
                      {isRTL ? "نعم" : "Yes"}
                    </button>
                    <button
                      onClick={onDeleteCancel}
                      style={{ padding: "5px 12px", borderRadius: "8px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: "transparent", color: theme === "dark" ? "#fff" : "#374151", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                    >
                      {t.cancel}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "8px" }}>
                    {canEdit && (
                    <button
                      onClick={() => onStartEdit(holiday)}
                      title={t.editHoliday}
                      style={{ padding: "6px 10px", borderRadius: "8px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.12)" : "#d1d5db"}`, background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff", color: "#2e7cba", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600" }}
                    >
                      <Settings size={13} /> {t.editHoliday}
                    </button>
                    )}
                    {canRemove && (
                    <button
                      onClick={() => onDeleteConfirm(holiday.id)}
                      title={t.deleteHoliday}
                      style={{ padding: "6px 10px", borderRadius: "8px", border: "1px solid rgba(239,68,68,0.3)", background: theme === "dark" ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.06)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600" }}
                    >
                      <Trash2 size={13} /> {t.deleteHoliday}
                    </button>
                    )}
                  </div>
                )}
              </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HolidayTable;